import {ref} from 'vue';
import {defineStore} from 'pinia';
import {useAsyncData, useDataTracking, useLogger, useMaltVisitorId, useResponseHeader, useUniversalFetch} from '#imports';

type Campaigns = Record<string, string>;

export const useABTestStore = defineStore('nuxt-features-module-ab-test-store', () => {
    const campaigns = ref<Campaigns>();
    const logger = useLogger();
    const maltVisitorId = useMaltVisitorId();
    const {track} = useDataTracking();

    async function init() {
        if (!maltVisitorId.value) {
            logger.error('Trying to fetch campaigns variations without Malt visitor ID');
        }

        const {data, error} = await useAsyncData('abtest-campaigns-variations', () =>
            useUniversalFetch<Campaigns>(`/public-api/experiment/campaigns?maltVisitorId=${maltVisitorId.value}`),
        );

        if (error.value || !data.value) {
            logger.error(`Error while fetching campaigns variations for visitor ${maltVisitorId.value}`, error.value);
            return;
        }

        campaigns.value = data.value;
    }

    function getVariation(campaignId: string): string | undefined {
        const variationId = getVariationWithoutTracking(campaignId);
        if (variationId) {
            /**
             * Ensure we don't store this page in cache when an AB test campaign is running
             * See `usePageCache` composable for more information
             */
            const cacheControlHeader = useResponseHeader('Cache-Control');
            cacheControlHeader.value = `no-store`;

            // eslint-disable-next-line @malt/tracking-event-name
            track('$exposure', {flag_key: campaignId, variant: variationId});
        }
        return variationId;
    }

    function getVariationWithoutTracking(campaignId: string): string | undefined {
        if (!campaigns.value) {
            logger.error(
                'Unable to get campaign variation before initializing the store. Make sure to set `loadABTestCampaigns` to `true` in your Nuxt configuration.',
            );
            return;
        }

        return campaigns.value[campaignId as keyof typeof campaigns.value];
    }

    return {
        init,
        getVariationWithoutTracking,
        getVariation,
    };
});
